import React, { useState, useEffect, Component } from 'react';
import { TrendingDown, Save, BarChart3, AlertTriangle, Check } from 'lucide-react';
import { botAPI } from '../services/api';

// Error boundary to prevent crashes
class DCAErrorBoundary extends Component {
    constructor(props) { super(props); this.state = { hasError: false }; }
    static getDerivedStateFromError() { return { hasError: true }; }
    componentDidCatch(err) { console.error('[DCASettings] Render error:', err); }
    render() {
        if (this.state.hasError) {
            return <div className="card p-6"><p className="text-accent-red text-sm">DCA Settings failed to load. Check console.</p></div>;
        }
        return this.props.children;
    }
}

function DCASettingsInner() {
    const [config, setConfig] = useState({
        enabled: false,
        minDropPercent: 3,
        maxDropPercent: 25,
        dcaSizePercent: 50,
        maxDcaRounds: 3,
        maxPositionPercent: 10,
        minTimeBetweenDca: 60,
        onlyPositiveScore: true,
    });
    const [stats, setStats] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadConfig();
        loadStats();
    }, []);

    const loadConfig = async () => {
        try {
            const res = await botAPI.getDcaConfig();
            setConfig(c => ({ ...c, ...res.data }));
        } catch (err) { console.error('Failed to load DCA config'); }
    };

    const loadStats = async () => {
        try {
            const res = await botAPI.getDcaStats();
            setStats(res.data);
        } catch (err) { /* no stats yet */ }
    };

    const saveConfig = async (overrides = {}) => {
        setSaving(true); setError(null); setSaved(false);
        const toSave = { ...config, ...overrides };
        try {
            const res = await botAPI.updateDcaConfig(toSave);
            if (res.data && res.data.success) {
                setConfig(toSave);
                setSaved(true);
                setTimeout(() => setSaved(false), 3000);
            } else {
                setError('Unexpected response');
            }
        } catch (err) {
            console.error('[DCA] Save error:', err);
            setError(err.response?.data?.error || err.message || 'Failed to save');
        }
        setSaving(false);
    };

    const toggleEnabled = () => {
        const newEnabled = !config.enabled;
        updateField('enabled', newEnabled);
        saveConfig({ enabled: newEnabled });
    };

    const updateField = (field, value) => {
        setConfig(c => ({ ...c, [field]: value }));
    };

    const Slider = ({ label, description, field, min, max, step, unit, format }) => (
        <div className="py-3 border-b border-border-color/30 last:border-0">
            <div className="flex justify-between mb-1">
                <div>
                    <div className="text-sm font-medium">{label}</div>
                    {description && <div className="text-xs text-text-muted">{description}</div>}
                </div>
                <div className="text-sm font-mono text-accent-purple">
                    {format ? format(config[field]) : config[field]}{unit || ''}
                </div>
            </div>
            <input type="range" min={min} max={max} step={step || 1} value={config[field]}
                onChange={e => updateField(field, parseFloat(e.target.value))}
                className="w-full h-1.5 bg-bg-tertiary rounded-lg appearance-none cursor-pointer accent-accent-purple" />
            <div className="flex justify-between text-xs text-text-muted mt-0.5">
                <span>{min}{unit || ''}</span>
                <span>{max}{unit || ''}</span>
            </div>
        </div>
    );

    const Toggle = ({ label, description, field }) => (
        <div className="flex items-center justify-between py-3 border-b border-border-color/30 last:border-0">
            <div>
                <div className="text-sm font-medium">{label}</div>
                {description && <div className="text-xs text-text-muted">{description}</div>}
            </div>
            <button onClick={() => updateField(field, !config[field])}
                className={`relative w-12 h-6 rounded-full transition-colors ${config[field] ? 'bg-accent-purple' : 'bg-bg-tertiary'}`}>
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${config[field] ? 'left-6' : 'left-0.5'}`} />
            </button>
        </div>
    );

    // Example calculation
    const exampleDrop = config.minDropPercent;
    const exampleEntry = 100;
    const exampleCurrent = exampleEntry * (1 - exampleDrop / 100);
    const exampleDcaShares = Math.round(config.dcaSizePercent / 100 * 10); // assume 10 original shares
    const newAvg = ((exampleEntry * 10) + (exampleCurrent * exampleDcaShares)) / (10 + exampleDcaShares);

    return (
        <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <TrendingDown className="w-6 h-6 text-accent-purple" />
                    <div>
                        <h2 className="text-lg font-bold">Dollar Cost Averaging (DCA)</h2>
                        <p className="text-xs text-text-muted">Automatically average down on losing positions to lower cost basis</p>
                    </div>
                </div>
                <button onClick={toggleEnabled}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                        config.enabled
                            ? 'bg-accent-purple/20 text-accent-purple border border-accent-purple/30'
                            : 'bg-bg-tertiary text-text-secondary border border-border-color'}`}>
                    {config.enabled ? '● Enabled' : '○ Disabled'}
                </button>
            </div>

            {/* Stats */}
            {stats && stats.totalDcaBuys > 0 && (
                <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-bg-tertiary rounded-lg p-3 text-center">
                        <div className="text-lg font-bold text-accent-purple">{stats.totalDcaBuys}</div>
                        <div className="text-xs text-text-muted">DCA Buys</div>
                    </div>
                    <div className="bg-bg-tertiary rounded-lg p-3 text-center">
                        <div className="text-lg font-bold">{stats.activeDcaPositions}</div>
                        <div className="text-xs text-text-muted">Active</div>
                    </div>
                    <div className="bg-bg-tertiary rounded-lg p-3 text-center">
                        <div className="text-lg font-bold">${stats.totalInvested?.toFixed(0) || 0}</div>
                        <div className="text-xs text-text-muted">DCA Invested</div>
                    </div>
                </div>
            )}

            {/* Settings */}
            <div className="space-y-1">
                <Slider label="Min Drop to Trigger" description="Position must be down at least this much"
                    field="minDropPercent" min={1} max={15} step={0.5} unit="%" />
                
                <Slider label="Max Drop Cutoff" description="Don't DCA if position is down more than this (cut losses instead)"
                    field="maxDropPercent" min={10} max={50} step={1} unit="%" />

                <Slider label="DCA Buy Size" description="Each DCA buy as % of original position size"
                    field="dcaSizePercent" min={10} max={200} step={10} unit="%" />

                <Slider label="Max DCA Rounds" description="Maximum number of DCA buys per position"
                    field="maxDcaRounds" min={1} max={10} step={1} unit="x" />

                <Slider label="Max Total Position" description="Max position size as % of portfolio after DCA"
                    field="maxPositionPercent" min={3} max={25} step={1} unit="%" />

                <Slider label="Min Time Between DCA" description="Minimum minutes between DCA buys on same symbol"
                    field="minTimeBetweenDca" min={15} max={480} step={15} unit=" min"
                    format={v => v >= 60 ? `${(v/60).toFixed(1)}h` : `${v}`} />

                <Toggle label="Only DCA Positive AI Score" description="Only average down if AI score is still bullish (>50%)"
                    field="onlyPositiveScore" />
            </div>

            {/* Example */}
            <div className="mt-4 p-3 bg-bg-tertiary rounded-lg text-xs text-text-muted">
                <div className="flex items-center gap-1 font-medium text-text-secondary mb-1">
                    <BarChart3 className="w-3 h-3" /> Example
                </div>
                <p>Stock bought at $100, drops {exampleDrop}% to ${exampleCurrent.toFixed(2)} → DCA buys {exampleDcaShares} more shares ({config.dcaSizePercent}% of original) → New avg cost: ${newAvg.toFixed(2)} — only needs to recover to ${newAvg.toFixed(2)} instead of $100 to break even.</p>
            </div>

            {/* Save */}
            <div className="flex items-center gap-3 mt-4">
                <button onClick={saveConfig} disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-accent-purple text-white rounded-lg text-sm font-medium disabled:opacity-50">
                    <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save DCA Settings'}
                </button>
                {saved && <span className="flex items-center gap-1 text-sm text-accent-green"><Check className="w-4 h-4" /> Saved!</span>}
                {error && <span className="flex items-center gap-1 text-sm text-accent-red"><AlertTriangle className="w-4 h-4" /> {error}</span>}
            </div>
        </div>
    );
}

export default function DCASettings() {
    return <DCAErrorBoundary><DCASettingsInner /></DCAErrorBoundary>;
}
